import { Component, ElementRef, EventEmitter, forwardRef, HostBinding, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

function parseNumber<T>(value: any, def: T): number | T {
    if (typeof value === 'string') {
        value = parseFloat(value);
    } else if (typeof value !== 'number') {
        value = def;
    }

    return isNaN(value) ? def : value;
}

@Component({
    selector: 'app-input-number',
    templateUrl: './input-number.component.html',
    styleUrls: ['./input-number.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputNumberComponent),
            multi: true
        }
    ]
})
export class InputNumberComponent implements ControlValueAccessor {
    @Input() changeByClickOnly;
    @Input() canSubBeDisabled;
    options = {
        step: 1,
        min: null,
        max: null,
        disabled: false,
        readonly: false
    };

    @HostBinding('class.input-number') class = true;

    @Input() size: 'sm' | 'lg' = null;

    @Input() set step(value: number) {
        this.options.step = parseNumber(value, 1);
    }

    @Input() set min(value: number) {
        this.options.min = parseNumber(value, null);
    }

    @Input() set max(value: number) {
        this.options.max = parseNumber(value, null);
    }

    @Input() set disabled(value: boolean) {
        this.options.disabled = !!value;
    }

    @Input() set readonly(value: boolean) {
        this.options.readonly = !!value;
    }

    @Output() changed = new EventEmitter();

    @ViewChild('inputElement', { static: true }) inputElementRef: ElementRef;

    get inputElement(): HTMLInputElement {
        return this.inputElementRef.nativeElement;
    }

    get value(): '' | number {
        return this.inputElement.value === '' ? '' : parseFloat(this.inputElement.value);
    }
    set value(value: '' | number) {
        this.writeValue(value);
    }

    onChange = (_: any) => { };
    onTouched = () => { };

    constructor() { }

    add(): void {
        if (this.options.disabled || this.options.readonly)
            return;

        let value = 1;
        try {
            value = Number(this.value);
        } catch (err) {}
        if (this.options.max && (value + this.options.step > this.options.max))
            return;
        this.change(1);
        this.changeByTimer(1);
    }

    sub(): void {
        if (this.options.disabled || this.options.readonly)
            return;
        this.change(-1);
        this.changeByTimer(-1);
    }

    input(): void {
        // @ts-ignore
        let value: any = this.inputElement.value.split('').filter(char => char >= 0 && char < 10).join('');

        if (this.options.max !== null && value) {
            value = Math.min(this.options.max, value);
        }
        if (this.options.min !== null && value) {
            value = Math.max(this.options.min, value);
        }

        this.onChange(value);
        this.value = value;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    writeValue(obj: any): void {
        if (typeof obj === 'number') {
            this.inputElement.value = obj.toString();
        } else {
            this.inputElement.value = '';
        }
    }

    /**
     * @param direction - one of [-1, 1]
     */
    private change(direction: number): void {
        let value = (this.value === '' || isNaN(this.value) ? 0 : this.value) + this.options.step * direction;

        let emitted = false;
        if (this.options.min > value) {
            this.changed.emit(0);
            emitted = true;
        }
        if (this.options.max !== null) {
            if (value > this.options.max)
                return;
            value = Math.min(this.options.max, value);
        }
        if (this.options.min !== null) {
            value = Math.max(this.options.min, value);
        }

        if (!emitted)
            this.changed.emit(value);

        if (value !== this.value) {
            this.onChange(value);
            this.value = value;
        }
    }

    /**
     * @param direction - one of [-1, 1]
     */
    private changeByTimer(direction: number): void {
        let interval;
        const timer = setTimeout(() => {
            interval = setInterval(() => this.change(direction), 50);
        }, 300);

        const documentMouseUp = () => {
            clearTimeout(timer);
            clearInterval(interval);

            document.removeEventListener('mouseup', documentMouseUp, false);
        };

        document.addEventListener('mouseup', documentMouseUp, false);
    }

    isAddDisabled(val) {
        return this.options.max !== null && ((Number(val) || 0) + this.options.step > this.options.max);
    }
}
